import { useState, useEffect, useCallback } from 'react';

export interface UseSearchCommandOptions {
  /** Prevent default browser behavior for Cmd/Ctrl+K */
  preventDefault?: boolean;
  /** Callback when search opens */
  onOpen?: () => void;
  /** Callback when search closes */
  onClose?: () => void;
}

export interface UseSearchCommandReturn {
  /** Whether the search command is open */
  isOpen: boolean;
  /** Open the search command */
  open: () => void;
  /** Close the search command */
  close: () => void;
  /** Toggle the search command */
  toggle: () => void;
}

/**
 * useSearchCommand - Hook to manage search command palette state
 *
 * Automatically handles the global Cmd/Ctrl+K keyboard shortcut
 * to open/close the search palette.
 *
 * @example
 * ```tsx
 * function App() {
 *   const { isOpen, open, close } = useSearchCommand();
 *
 *   return (
 *     <>
 *       <SearchTrigger onClick={open} />
 *       <SearchCommand isOpen={isOpen} onClose={close} results={results} />
 *     </>
 *   );
 * }
 * ```
 */
export function useSearchCommand(
  options: UseSearchCommandOptions = {}
): UseSearchCommandReturn {
  const { preventDefault = true, onOpen, onClose } = options;
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => {
    setIsOpen(true);
    onOpen?.();
  }, [onOpen]);

  const close = useCallback(() => {
    setIsOpen(false);
    onClose?.();
  }, [onClose]);

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      const next = !prev;
      if (next) {
        onOpen?.();
      } else {
        onClose?.();
      }
      return next;
    });
  }, [onOpen, onClose]);

  // Global Cmd/Ctrl+K shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        if (preventDefault) {
          e.preventDefault();
        }
        toggle();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [preventDefault, toggle]);

  return {
    isOpen,
    open,
    close,
    toggle,
  };
}

export default useSearchCommand;
